/**
 * `next/dynamic` shim for the Vite + RR7 frontend.
 *
 * The legacy code uses `dynamic(() => import('...'), { ssr: false, loading })`
 * extensively to defer heavy modules (Vidstack player, Jassub, motion,
 * ad manager, etc.) until after first paint. With React Router 7 + manual
 * SSR we get the same behavior by combining `React.lazy` with `Suspense`
 * plus a browser-only guard for components that must not render on the
 * server.
 *
 * The returned component preserves Next's call-site API as closely as
 * possible so import sites only need their path swapped.
 */
import {
  type ComponentType,
  type ReactNode,
  lazy,
  Suspense,
  useEffect,
  useState,
} from 'react';

export interface DynamicOptions<P = unknown> {
  ssr?: boolean;
  // Next's docs say `loading` is `() => JSX.Element | null`, but some
  // call sites in this codebase pass the JSX element directly. Accept
  // both shapes — `renderLoading` below normalizes them.
  loading?: ReactNode | (() => ReactNode);
}

function renderLoading(loading: DynamicOptions['loading']): ReactNode {
  if (!loading) return null;
  if (typeof loading === 'function') return loading();
  return loading;
}

// Permissive loader signature. Next's `dynamic` accepts any of:
//   () => import('foo')                          // → { default: X }
//   () => import('foo').then(m => m.Named)       // → X
// — both shapes are handled at runtime by normalizeLoader. Typing this
// strictly forces TS to pick one branch and rejects valid call sites.
type Loader<P> = () => Promise<unknown>;

function normalizeLoader<P>(
  loader: Loader<P>
): () => Promise<{ default: ComponentType<P> }> {
  return async () => {
    const mod = await loader();
    if (
      mod &&
      typeof mod === 'object' &&
      'default' in (mod as Record<string, unknown>)
    ) {
      return mod as { default: ComponentType<P> };
    }
    return { default: mod as ComponentType<P> };
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ClientOnly({
  Component,
  loading,
  ...props
}: {
  Component: ComponentType<any>;
  loading?: DynamicOptions['loading'];
  [key: string]: unknown;
}): ReactNode {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) return renderLoading(loading);
  return <Component {...props} />;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally
// permissive: Next's `dynamic` is typed loose enough to accept loaders that
// resolve to either the component directly or `{ default: ... }`, and to
// preserve P inference at call sites without coercion. Matching that shape.
export default function dynamic<P = any>(
  loader: Loader<P>,
  options: DynamicOptions<P> = {}
): ComponentType<P> {
  const Lazy = lazy(normalizeLoader<P>(loader));
  const { loading, ssr = true } = options;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function DynamicComponent(props: any): ReactNode {
    if (ssr === false) {
      return (
        <ClientOnly Component={Lazy} loading={loading} {...props} />
      );
    }
    return (
      <Suspense fallback={renderLoading(loading)}>
        <Lazy {...props} />
      </Suspense>
    );
  }

  // Preserve a useful displayName for the React devtools.
  (DynamicComponent as { displayName?: string }).displayName = 'Dynamic';
  return DynamicComponent;
}
